import { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';

import type { APIData, RecordData, RequestError, Service } from 'webapp-types';
import { ModelName } from 'webapp/enums/enums';

import Board from 'webapp/models-custom/board';
import type BoardPreviewRecord from 'webapp/models/board-preview';

import ModelManagerForStoreService from 'webapp/services/model-manager/for-store';

export default class ModelManagerBoardPreviewsService extends ModelManagerForStoreService<
	BoardPreviewRecord,
	Board
> {
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('request') declare requestService: Service.Request;
	@service('socket') declare socketService: Service.Socket;
	@service('garbage-collector') declare garbageCollectorService: Service.GarbageCollector;

	private _boardModels: Board[] = [];

	@cached
	get boardModels(): Board[] {
		this.updateModelCache(this._boardModels, this.savedRecords);
		return this._boardModels;
	}

	getNewPreviewHash(): Promise<string> {
		return new Promise((resolve, reject) => {
			this.requestService
				.api<string>('GET', '/previews/hash')
				.then(
					(hash) => {
						resolve(hash);
					},
					(errorData) => {
						reject();
						throw errorData;
					},
				)
				.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
		});
	}

	// returns promise that resolves when channel is established
	getPromiseAfterPreviewDataChannelSubscribed(previewHash = ''): Promise<unknown> {
		const socketService = this.socketService;

		if (previewHash) {
			return new Promise((resolve, reject) => {
				socketService.afterPusherAuthorized?.then(
					() => {
						const channel = socketService.subscribeToBoardPreview(previewHash);

						if (channel) {
							if (channel.subscribed) {
								resolve(previewHash);
							} else {
								channel.bind('pusher:subscription_succeeded', () => {
									resolve(previewHash);
								});
								channel.bind('pusher:subscription_error', () => {
									// this.logger.logError('Board Preview channel subscription_error', {status}, Severity.CRITICAL);
									reject('channel');
								});
							}
						} else {
							reject('channel');
						}
					},
					() => {
						reject('socket');
					},
				);
			});
		} else {
			return socketService.afterDataChannelSubscribed!;
		}
	}

	// ---------- requested previews cache ------------

	@tracked private _previewHashesRequested: string[] = [];
	private _previewRequestPromises: Record<string, Promise<boolean>> = {};

	requestPreview(previewHash: string, boardId: number): Promise<boolean> {
		let promise;

		// first check if already requested
		if (this._previewHashesRequested.includes(previewHash)) {
			promise = Promise.resolve(true);
		} else {
			// check if requesting is in progress
			promise = this._previewRequestPromises[previewHash];

			if (!promise) {
				// make request
				promise = new Promise<boolean>((resolve) => {
					this.requestService
						.api<APIData.Board.PreviewsResponse>('GET', `/previews/${previewHash}`, {
							boardId,
						})
						.then((reply) => {
							this.storeService.pushPayload(reply);

							// also request data, but only after pusher channel subscribed
							void this.getPromiseAfterPreviewDataChannelSubscribed(previewHash).then(
								() => {
									void this.requestService
										.api<RecordData.Payload>(
											'GET',
											`/previews/${previewHash}/data`,
											{ boardId },
										)
										.then((payload) => {
											this.storeService.pushPayload(payload);
										});
								},
							);

							this._previewHashesRequested = [
								...this._previewHashesRequested,
								previewHash,
							];

							resolve(true);
						})
						.catch((errorData: RequestError) => {
							this.requestService.handleGenericThrownError(errorData);
							resolve(false);
						})
						.finally(() => {
							delete this._previewRequestPromises[previewHash];
						});
				});

				this._previewRequestPromises[previewHash] = promise;
			}
		}

		return promise;
	}

	isPreviewDataRequested(previewHash: string): boolean {
		return this._previewHashesRequested.includes(previewHash);
	}

	markPreviewRequested(previewHash: string): void {
		if (!this.isPreviewDataRequested(previewHash)) {
			this._previewHashesRequested = [...this._previewHashesRequested, previewHash];
		}
	}

	clearAllRequestedPreviews(): void {
		// clear all promises, requested ids, open socket channels
		// clear all board preview data and samples

		const clearBoardPreviewData = (previewHash: string) => {
			this.socketService.unsubscribeFromBoardPreview(previewHash);
			this.garbageCollectorService._unloadBoardData('x', previewHash);
			const record = this.findRecordByHash(previewHash);
			if (record) {
				record.unloadRecord();
			}
		};

		Object.entries(this._previewRequestPromises).forEach(([previewHash, promise]) => {
			void promise.then(() => {
				this._previewHashesRequested = this._previewHashesRequested.filter(
					(hash) => hash !== previewHash,
				);
				clearBoardPreviewData(previewHash);
			});
		});
		this._previewRequestPromises = {};

		this._previewHashesRequested.forEach(clearBoardPreviewData);
		this._previewHashesRequested = [];
	}

	// ------ For-store ------

	modelName = ModelName.BOARD_PREVIEW;

	findRecordByHash(previewHash: string): BoardPreviewRecord | null {
		const record = this.storeService
			.peekAll(this.modelName)
			.find((item) => (item as BoardPreviewRecord).previewHash === previewHash);
		return record as BoardPreviewRecord | null;
	}

	findModelByHash(previewHash: string): Board | null {
		let model;
		const record = this.findRecordByHash(previewHash);
		if (record) {
			model = this.createModelFromRecord(record);
		}
		return model || null;
	}

	createModel(model: BoardPreviewRecord): Board {
		return new Board({ model });
	}
}

declare module '@ember/service' {
	interface Registry {
		'model-manager/board-previews': ModelManagerBoardPreviewsService;
	}
}
